// Post Preview Component
import React from 'react';
import { X } from 'lucide-react';
import { SocialPlatform } from '../types/social.types';

interface Props {
  content: string;
  platforms: SocialPlatform[];
  images?: string[];
  onClose: () => void;
}

const PostPreview: React.FC<Props> = ({ content, platforms, images, onClose }) => {
  const renderFacebookPreview = () => (
    <div className="bg-white rounded-lg shadow-md p-4 max-w-lg">
      <div className="flex items-center mb-3">
        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
          PS
        </div>
        <div className="ml-3">
          <p className="font-semibold">PlexiSystem</p>
          <p className="text-xs text-gray-500">Teraz</p>
        </div>
      </div>
      <p className="text-gray-800 whitespace-pre-wrap mb-3">{content}</p>
      {images && images.length > 0 && (
        <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center text-gray-500">
          <p>Obrazy ({images.length})</p>
        </div>
      )}
      <div className="mt-3 pt-3 border-t flex justify-between text-gray-500">
        <button className="hover:text-blue-600">👍 Lubię to</button>
        <button className="hover:text-blue-600">💬 Komentarz</button>
        <button className="hover:text-blue-600">↗️ Udostępnij</button>
      </div>
    </div>
  );

  const renderLinkedInPreview = () => (
    <div className="bg-white rounded-lg shadow-md p-4 max-w-lg">
      <div className="flex items-center mb-3">
        <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center text-white font-bold">
          PS
        </div>
        <div className="ml-3">
          <p className="font-semibold">PlexiSystem</p>
          <p className="text-sm text-gray-600">Firma • Produkcja</p>
          <p className="text-xs text-gray-500">Teraz • 🌍</p>
        </div>
      </div>
      <p className="text-gray-800 whitespace-pre-wrap mb-3">{content}</p>
      {images && images.length > 0 && (
        <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center text-gray-500 mb-3">
          <p>Multimedia</p>
        </div>
      )}
      <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-3">
        <span>👍 💡 ❤️ 42</span>
        <span>8 komentarzy • 3 udostępnienia</span>
      </div>
    </div>
  );

  const renderTwitterPreview = () => {
    const truncatedContent = content.length > 280 ? content.substring(0, 277) + '...' : content;
    
    return (
      <div className="bg-white rounded-lg shadow-md p-4 max-w-lg">
        <div className="flex">
          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 font-bold">
            PS
          </div>
          <div className="ml-3 flex-1">
            <div className="flex items-center">
              <p className="font-bold">PlexiSystem</p>
              <p className="text-gray-500 ml-1">@plexisystem · teraz</p>
            </div>
            <p className="text-gray-800 mt-1 whitespace-pre-wrap">{truncatedContent}</p>
            {content.length > 280 && (
              <p className="text-sm text-red-500 mt-1">
                ⚠️ Post przekracza limit 280 znaków
              </p>
            )}
            {images && images.length > 0 && (
              <div className="bg-gray-200 h-48 rounded-lg mt-3 flex items-center justify-center text-gray-500">
                <p>Obrazy ({images.length})</p>
              </div>
            )}
            <div className="flex justify-between mt-3 text-gray-500">
              <button className="hover:text-blue-500">💬 0</button>
              <button className="hover:text-green-500">🔁 0</button>
              <button className="hover:text-red-500">❤️ 0</button>
              <button className="hover:text-blue-500">📊 0</button>
              <button className="hover:text-blue-500">↗️</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInstagramPreview = () => (
    <div className="bg-white rounded-lg shadow-md max-w-sm">
      <div className="p-3 flex items-center">
        <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
          PS
        </div>
        <p className="ml-3 font-semibold text-sm">plexisystem</p>
      </div>
      <div className="bg-gray-200 h-96 flex items-center justify-center text-gray-500">
        {images && images.length > 0 ? (
          <p>Obrazy ({images.length})</p>
        ) : (
          <p>Brak obrazu</p>
        )}
      </div>
      <div className="p-3">
        <div className="flex justify-between mb-3">
          <div className="flex gap-3">
            <button>🤍</button>
            <button>💬</button>
            <button>📤</button>
          </div>
          <button>🔖</button>
        </div>
        <p className="text-sm">
          <span className="font-semibold mr-2">plexisystem</span>
          <span className="whitespace-pre-wrap">{content}</span>
        </p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-100 rounded-xl max-w-6xl max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Podgląd posta</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {platforms.includes('facebook') && (
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-3">
                  📘 Facebook
                </h3>
                {renderFacebookPreview()}
              </div>
            )}
            
            {platforms.includes('linkedin') && (
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-3">
                  💼 LinkedIn
                </h3>
                {renderLinkedInPreview()}
              </div>
            )}
            
            {platforms.includes('twitter') && (
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-3">
                  🐦 Twitter/X
                </h3>
                {renderTwitterPreview()}
              </div>
            )}
            
            {platforms.includes('instagram') && (
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-3">
                  📸 Instagram
                </h3>
                {renderInstagramPreview()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostPreview;
